/**
 * Support Tickets Section (Smart Component)
 * Owns all Redux state, dispatch, and selector logic.
 * Passes data and callbacks down to dumb components.
 */

import { useState, useCallback, useEffect, useMemo, lazy, Suspense } from 'react'
import { useTranslation, useUrlFilterSync } from '@ufc/core'
import { useDispatch, useSelector } from '@ufc/core'
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  PageHeader,
  Pagination,
  Search,
  Plus,
} from '@ufc/uikit'
import { selectUserEmail, selectUserDisplayName, selectUserRoleType, ROLE_TYPES } from '@ufc/domains/auth'
import { factoriesActions, selectFactoriesList } from '@ufc/domains/factories'

import { supportActions } from '../../../store/support.slice'
import {
  selectSupportTickets,
  selectSupportStats,
  selectSupportFilter,
  selectSupportPagination,
  selectSupportLoading,
  selectSelectedTicket,
} from '../../../store/support.selectors'
import { supportFilterConfig } from '../../../domain/filter-config'
import type { SupportTicket, TicketStatus, TicketSlaStatus, SupportTicketNote } from '../../../domain/types'

import { SupportTicketsStats } from '../../components/support-tickets-stats'
import { SupportTicketsTable } from '../../components/support-tickets-table'
import type { SubmitTicketFormData } from '../../drawers/submit-ticket-drawer'
const SubmitTicketDrawer = lazy(() => import('../../drawers/submit-ticket-drawer').then(m => ({ default: m.SubmitTicketDrawer })))
const TicketDetailDrawer = lazy(() => import('../../drawers/ticket-detail-drawer').then(m => ({ default: m.TicketDetailDrawer })))

type ImageFilePicker = () => Promise<File[]>

interface SupportTicketsSectionProps {
  pickImageFiles?: ImageFilePicker
}

export function SupportTicketsSection({ pickImageFiles }: SupportTicketsSectionProps = {}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Redux selectors
  const tickets = useSelector(selectSupportTickets)
  const stats = useSelector(selectSupportStats)
  const filter = useSelector(selectSupportFilter)
  const pagination = useSelector(selectSupportPagination)
  const loading = useSelector(selectSupportLoading)
  const selectedTicket = useSelector(selectSelectedTicket)

  // Auth selectors
  const userEmail = useSelector(selectUserEmail)
  const userDisplayName = useSelector(selectUserDisplayName)
  const roleType = useSelector(selectUserRoleType)
  const canViewSla = roleType === ROLE_TYPES.ADMIN || roleType === ROLE_TYPES.SUPER_ADMIN

  // Factory list for ticket creation
  const factoriesList = useSelector(selectFactoriesList)
  const factoryOptions = useMemo(
    () => factoriesList.map((f) => ({ documentId: f.documentId, name: f.name, code: f.code })),
    [factoriesList]
  )

  // Local UI state
  const [showCreateDrawer, setShowCreateDrawer] = useState(false)

  useEffect(() => {
    if (factoriesList.length === 0) {
      dispatch(factoriesActions.fetchFactoriesList())
    }
  }, [dispatch, factoriesList.length])

  // URL filter sync
  useUrlFilterSync({
    currentFilter: filter,
    config: supportFilterConfig,
    onFilterChange: (newFilter) => {
      dispatch(supportActions.setFilter(newFilter))
      dispatch(supportActions.fetchTickets(newFilter))
    },
  })

  // --- Filter handlers ---

  const handleSearch = useCallback(
    (value: string) => {
      dispatch(supportActions.searchTickets(value))
      dispatch(supportActions.fetchTickets())
    },
    [dispatch]
  )

  const handleStatusFilter = useCallback(
    (status: string) => {
      dispatch(supportActions.filterByStatus([status]))
      dispatch(supportActions.fetchTickets())
    },
    [dispatch]
  )

  const handleChannelFilter = useCallback(
    (channel: string) => {
      const value = channel === 'all' ? undefined : channel
      dispatch(supportActions.filterByChannel(value))
      dispatch(supportActions.fetchTickets())
    },
    [dispatch]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(supportActions.setPage(page))
      dispatch(supportActions.fetchTickets())
    },
    [dispatch]
  )

  // --- Ticket CRUD handlers ---

  const handleTicketSelect = useCallback(
    (ticket: SupportTicket) => {
      dispatch(supportActions.setSelectedTicket(ticket))
    },
    [dispatch]
  )

  const handleCreateSubmit = useCallback(
    (data: SubmitTicketFormData) => {
      dispatch(supportActions.createTicket(data))
      setShowCreateDrawer(false)
    },
    [dispatch]
  )

  const handleDetailClose = useCallback(() => {
    dispatch(supportActions.setSelectedTicket(null))
  }, [dispatch])

  const handleStatusChange = useCallback(
    (ticketId: string, status: TicketStatus, slaStatus?: TicketSlaStatus) => {
      dispatch(supportActions.updateTicket({ id: ticketId, status, ...(slaStatus ? { slaStatus } : {}) }))
    },
    [dispatch]
  )

  const handleAddNote = useCallback(
    (ticketId: string, text: string) => {
      const ticket = tickets.find((t) => t.documentId === ticketId) ?? selectedTicket
      if (!ticket) return

      const newNote: SupportTicketNote = {
        author: userDisplayName || 'Support Agent',
        text,
        createdAt: new Date().toISOString(),
      }
      const updatedNotes = [...(ticket.notes ?? []), newNote]
      dispatch(supportActions.updateTicket({ id: ticketId, notes: updatedNotes }))
    },
    [dispatch, tickets, selectedTicket, userDisplayName]
  )

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={t('support.title', 'Support Tickets')}
        subtitle={t('support.subtitle', 'Manage customer and internal support requests')}
        actions={[
          {
            label: t('support.newTicket', 'New Ticket'),
            icon: Plus,
            onClick: () => setShowCreateDrawer(true),
            primary: true,
          },
        ]}
      />

      <SupportTicketsStats stats={stats} onStatusFilter={handleStatusFilter} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('support.searchPlaceholder', 'Search tickets...')}
            className="ps-9"
            defaultValue={filter.search ?? ''}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <Select value={filter.channel ?? 'all'} onValueChange={handleChannelFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('support.filterChannel', 'All Channels')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('support.allChannels', 'All Channels')}</SelectItem>
            <SelectItem value="customer_to_ufc">{t('support.channelCustomer', 'Customer')}</SelectItem>
            <SelectItem value="ufc_to_525k">{t('support.channelInternal', 'Internal')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <SupportTicketsTable tickets={tickets} onSelect={handleTicketSelect} showSla={canViewSla} />

      {/* Pagination */}
      {pagination.pageCount > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pageCount}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Submit Ticket Drawer (dumb) */}
      <Suspense fallback={null}>
        <SubmitTicketDrawer
          open={showCreateDrawer}
          onClose={() => setShowCreateDrawer(false)}
          onSubmit={handleCreateSubmit}
          loading={loading}
          channel="ufc_to_525k"
          sourcePortal="admin_portal"
          factories={factoryOptions}
          userEmail={userEmail}
          userDisplayName={userDisplayName}
          pickImageFiles={pickImageFiles}
        />
      </Suspense>

      {/* Ticket Detail Drawer (dumb) */}
      <Suspense fallback={null}>
        <TicketDetailDrawer
          open={!!selectedTicket}
          ticket={selectedTicket}
          loading={loading}
          onClose={handleDetailClose}
          onStatusChange={handleStatusChange}
          onAddNote={handleAddNote}
          canViewSla={canViewSla}
        />
      </Suspense>
    </div>
  )
}
